/**
 * The ⋮ menu of a project / PDApp card — the SAME catalog DriveView shows for a project or
 * PDApp folder (`buildFileActions`), as items for the shared glass `FileContextMenu`. Only
 * the actions whose handler the surface can wire appear, grouped with separators.
 *
 * ONE builder for every surface that offers it: Drive → Projects / PDApps cards
 * (`ProjectCard`) and the PDApps window's card back ("More"). `close` runs before each
 * action (the surface closes its menu, and whatever else it must, first).
 */
import { LayoutGrid } from 'lucide-react'
import type { FileMenuItem } from '@papan/core-ui/ui'
import { buildFileActions, type FileActionHandlers } from '../fileActions'
import type { FileItem } from '../useFiles'
import type { ProjectItem } from './data'
import type { ProjectCardHandlers } from './ProjectCard'

export function buildProjectMenuItems(project: ProjectItem, h: ProjectCardHandlers, opts: {
  t: (key: string, options?: Record<string, unknown>) => string
  /** A PDApp (Debug log is offered for one only). */
  isApp: boolean
  /** Before every action: close the menu (and anything else the surface closes). */
  close: () => void
}): FileMenuItem[] {
  const { t, isApp, close } = opts
  const canManage = !!project.access?.canManage
  const file = { id: project.folderId, name: project.name, isFolder: true } as FileItem
  const handlers: FileActionHandlers = { browse: () => h.onBrowse(project) }
  if (h.onEditSettings) handlers.editApp = () => h.onEditSettings!(project)
  if (h.onEditIcon) handlers.editIcon = () => h.onEditIcon!(project)
  if (h.onShare) handlers.shareLink = () => h.onShare!(project)
  if (h.onShareOrg) handlers.shareOrg = () => h.onShareOrg!(project)
  if (h.onWhoHasAccess) handlers.whoHasAccess = () => h.onWhoHasAccess!(project)
  // As the drive's own menus gate it (DriveView's rowCanManageAccess, the mobile sheet): an org
  // only (the handler is absent on a Personal drive), its managers, never a broken share.
  if (h.onShareInternally && canManage && !project.broken) handlers.shareInternally = () => h.onShareInternally!(project)
  if (h.onRename) handlers.rename = () => h.onRename!(project)
  if (h.onDuplicate) handlers.duplicate = () => h.onDuplicate!(project)
  if (h.onCopyTo) handlers.copyTo = () => h.onCopyTo!(project)
  if (h.onMoveTo) handlers.moveTo = () => h.onMoveTo!(project)
  if (h.onDownload) handlers.download = () => h.onDownload!(project)
  if (h.onDetails) handlers.details = () => h.onDetails!(project)
  if (h.onDebugLog && isApp) handlers.debugLog = () => h.onDebugLog!(project) // PDApp folders only
  if (h.onDelete) handlers.delete = () => h.onDelete!(project)
  // isOrg:true unconditionally so "Share to another Drive" + "Who has access"
  // also appear on personal drives (they're the only isOrg-gated actions, and
  // still gate on handler presence). Personal can share out + manage links/apps.
  const actions = buildFileActions(file, { t, isOrg: true, canWrite: canManage, handlers })
  const items: FileMenuItem[] = [
    { kind: 'item', icon: LayoutGrid, label: t('driveHome.projectsPage.openProject', { defaultValue: 'Open' }), testId: 'pcm-open', onClick: () => { close(); h.onOpen(project) } },
  ]
  let lastGroup: string | undefined
  for (const a of actions) {
    if (a.group !== lastGroup) { items.push({ kind: 'separator' }); lastGroup = a.group }
    // The card's icon-button wording ("Browse files") over the drive menu's "Browse as folder".
    const label = a.id === 'browse' ? t('driveHome.projects.browseFiles') : a.label
    items.push({ kind: 'item', icon: a.icon, label, danger: a.danger, testId: `pcm-${a.id}`, onClick: () => { close(); a.run() } })
  }
  return items
}
